
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePlayerStats } from '@/hooks/usePlayerStats';
import { supabase } from '@/integrations/supabase/client';

interface StatSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerId: string;
  gameId?: number;
}

const STAT_TYPES = [
  { value: 'points', label: 'Points' },
  { value: 'assists', label: 'Assists' },
  { value: 'rebounds', label: 'Rebounds' },
  { value: 'goals', label: 'Goals' },
  { value: 'saves', label: 'Saves' },
  { value: 'shots', label: 'Shots' },
  { value: 'fouls', label: 'Fouls' },
  { value: 'games_played', label: 'Games Played' }
];

const StatSubmissionModal = ({ isOpen, onClose, playerId, gameId }: StatSubmissionModalProps) => {
  const [selectedGameId, setSelectedGameId] = useState<number>(gameId || 0);
  const [selectedSportId, setSelectedSportId] = useState<number>(0);
  const [statType, setStatType] = useState('');
  const [statValue, setStatValue] = useState('');
  const [games, setGames] = useState<any[]>([]);
  const [sports, setSports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const { submitStat } = usePlayerStats(playerId);

  useEffect(() => {
    if (isOpen) {
      fetchGames();
      fetchSports();
    }
  }, [isOpen]);

  const fetchGames = async () => {
    try {
      const { data, error } = await supabase
        .from('events' as any)
        .select('event_id, event_name, event_date')
        .order('event_date', { ascending: false });

      if (error) {
        console.error('Error fetching games:', error);
        return;
      }

      setGames(data || []);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const fetchSports = async () => {
    try {
      const { data, error } = await supabase
        .from('sports' as any)
        .select('id, sport_name')
        .order('sport_name');

      if (error) {
        console.error('Error fetching sports:', error);
        return;
      }

      setSports(data || []);
    } catch (error) {
      console.error('Error fetching sports:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGameId || !selectedSportId || !statType || !statValue) {
      return;
    }

    setLoading(true);
    
    const success = await submitStat(
      selectedGameId,
      selectedSportId,
      statType,
      parseInt(statValue)
    );

    setLoading(false);

    if (success) {
      // Reset form
      setSelectedGameId(gameId || 0);
      setSelectedSportId(0);
      setStatType('');
      setStatValue('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Submit Player Stats</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="game" className="text-white">Game</Label>
            <Select 
              value={selectedGameId.toString()} 
              onValueChange={(value) => setSelectedGameId(parseInt(value))}
              disabled={!!gameId}
            >
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select a game" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {games.map((game) => (
                  <SelectItem key={game.event_id} value={game.event_id.toString()}>
                    {game.event_name} - {new Date(game.event_date).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="sport" className="text-white">Sport</Label>
            <Select value={selectedSportId.toString()} onValueChange={(value) => setSelectedSportId(parseInt(value))}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select a sport" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {sports.map((sport) => (
                  <SelectItem key={sport.id} value={sport.id.toString()}>
                    {sport.sport_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="statType" className="text-white">Stat Type</Label>
            <Select value={statType} onValueChange={setStatType}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select stat type" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {STAT_TYPES.map((stat) => (
                  <SelectItem key={stat.value} value={stat.value}>
                    {stat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="statValue" className="text-white">Stat Value</Label>
            <Input
              id="statValue"
              type="number"
              value={statValue}
              onChange={(e) => setStatValue(e.target.value)}
              placeholder="Enter stat value"
              className="bg-gray-700 border-gray-600 text-white"
              min="0"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedGameId || !selectedSportId || !statType || !statValue}
              className="bg-sports-green text-black hover:bg-green-600"
            >
              {loading ? 'Submitting...' : 'Submit Stats'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StatSubmissionModal;
